import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Star, Clock, CheckCircle, XCircle, WarningCircle, Question, TrendUp, TrendDown, CaretUp, CaretDown } from "@phosphor-icons/react";
import CompanyLogo from "@/components/CompanyLogo";
import { aaoifiScore, scoreColor } from "@/lib/aaoifi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT_COLORS = {
  compliant: "#22c55e",
  borderline: "#f59e0b",
  non_compliant: "#ef4444",
  unverified: "#71717a",
};
const VERDICT_LABELS = { compliant: "Compliant", borderline: "Borderline", non_compliant: "Non-Compliant", unverified: "Unverified" };

function VerdictPill({ verdict, testId }) {
  const c = VERDICT_COLORS[verdict] || VERDICT_COLORS.unverified;
  return (
    <span
      className="inline-flex px-2 py-[3px] text-[10px] font-mono font-semibold rounded-md whitespace-nowrap"
      style={{ color: c, background: `${c}14`, border: `1px solid ${c}33` }}
      data-testid={testId}
    >
      {VERDICT_LABELS[verdict] || "N/A"}
    </span>
  );
}

function ScoreCell({ stock, testId }) {
  const score = aaoifiScore(stock);
  if (score == null) return <span className="text-[10px] font-mono text-zinc-500 italic" data-testid={testId}>—</span>;
  return (
    <span className="text-[12px] font-mono tabular-nums whitespace-nowrap" data-testid={testId}>
      <span className="font-semibold" style={{ color: scoreColor(score) }}>{score}</span>
      <span className="text-zinc-500"> / 100</span>
    </span>
  );
}

function StatusIcon({ status, size = 12 }) {
  if (status === "pass") return <CheckCircle size={size} weight="fill" className="shrink-0" style={{ color: "#22c55e" }} />;
  if (status === "borderline") return <WarningCircle size={size} weight="fill" className="shrink-0" style={{ color: "#f59e0b" }} />;
  if (status === "unavailable") return <Question size={size} weight="fill" className="text-zinc-500 shrink-0" />;
  return <XCircle size={size} weight="fill" className="shrink-0" style={{ color: "#ef4444" }} />;
}

const RATIO_COLORS = { pass: "#22c55e", borderline: "#f59e0b", fail: "#ef4444", unavailable: "#52525b" };

function Ratio({ status, value, threshold, testId }) {
  const unavailable = status === "unavailable" || value === null || value === undefined;
  const color = RATIO_COLORS[status] || RATIO_COLORS.fail;
  const barPct = unavailable ? 0 : Math.min(100, Math.max(4, (Number(value) / (threshold * 2)) * 100));
  return (
    <div className="flex flex-col items-end gap-1" data-testid={testId}>
      {unavailable ? (
        <span className="text-[10px] font-mono text-zinc-500 italic">N/A</span>
      ) : (
        <span className="text-[11.5px] font-mono text-zinc-200 tabular-nums">{value}%</span>
      )}
      <div className="w-12 h-[3px] rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
        <div className="h-full rounded-full" style={{ width: `${barPct}%`, background: color }} />
      </div>
    </div>
  );
}

function fmtCap(v) {
  if (!v) return "-";
  if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  return `$${v.toLocaleString()}`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const mins = Math.floor((now - d) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/* Skeleton for price loading */
function PriceSkeleton() {
  return (
    <div className="flex flex-col items-end gap-1" data-testid="price-skeleton">
      <div className="h-3.5 w-14 animate-pulse rounded-sm" style={{ background: "rgba(255,255,255,0.06)" }} />
    </div>
  );
}

function ChangeSkeleton() {
  return (
    <div className="flex items-center gap-1 justify-end" data-testid="change-skeleton">
      <div className="h-3.5 w-16 animate-pulse rounded-sm" style={{ background: "rgba(255,255,255,0.06)" }} />
    </div>
  );
}

/* Price display cell */
function PriceCell({ quote }) {
  if (!quote) return <PriceSkeleton />;
  if (!quote.price) return <span className="text-[10px] font-mono text-zinc-500 italic">—</span>;
  return (
    <span className="text-[11.5px] font-mono text-zinc-100 tabular-nums" data-testid="price-value">
      ${quote.price.toFixed(2)}
    </span>
  );
}

/* Change display cell */
function ChangeCell({ quote }) {
  if (!quote) return <ChangeSkeleton />;
  if (!quote.price || (quote.change === 0 && quote.change_percent === 0)) {
    return <span className="text-[10px] font-mono text-zinc-500 italic">—</span>;
  }
  const isUp = quote.change >= 0;
  return (
    <div className="flex items-center gap-1 justify-end text-[11.5px] font-mono tabular-nums" style={{ color: isUp ? "#4ade80" : "#f87171" }} data-testid="change-value">
      {isUp ? <TrendUp size={12} weight="bold" className="shrink-0" /> : <TrendDown size={12} weight="bold" className="shrink-0" />}
      <span>{isUp ? "+" : ""}{quote.change_percent.toFixed(2)}%</span>
    </div>
  );
}

/* Mobile card view for small screens */
function MobileCard({ s, starred, onSelectStock, onStar, quote }) {
  const score = aaoifiScore(s);
  return (
    <div
      className="border-b py-3 px-2 cursor-pointer active:bg-white/[0.03] transition-colors"
      style={{ borderColor: "rgba(255,255,255,0.05)" }}
      onClick={() => onSelectStock(s)}
      data-testid={`stock-row-${s.ticker}`}
    >
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <button onClick={(e) => { e.stopPropagation(); onStar(s.ticker); }} className="p-0.5 shrink-0" data-testid={`star-${s.ticker}`}>
            <Star size={14} weight={starred ? "fill" : "regular"} className={starred ? "text-amber-500" : "text-zinc-600"} />
          </button>
          <CompanyLogo ticker={s.ticker} size={30} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono font-semibold text-zinc-100">{s.ticker}</span>
              {score != null && (
                <span className="text-[10px] font-mono tabular-nums">
                  <span style={{ color: scoreColor(score) }}>{score}</span>
                  <span className="text-zinc-500">/100</span>
                </span>
              )}
            </div>
            <div className="text-[11px] text-zinc-500 truncate">{s.company_name}</div>
          </div>
        </div>
        <VerdictPill verdict={s.verdict} testId={`verdict-badge-${s.ticker}`} />
      </div>
      <div className="flex items-center gap-4 ml-6 text-[11px] font-mono text-zinc-400">
        {quote && quote.price ? (
          <>
            <span className="text-zinc-100 font-medium">${quote.price.toFixed(2)}</span>
            <span style={{ color: quote.change >= 0 ? "#4ade80" : "#f87171" }}>
              {quote.change >= 0 ? "▲" : "▼"} {Math.abs(quote.change_percent).toFixed(2)}%
            </span>
          </>
        ) : (
          <span className="h-3 w-12 animate-pulse rounded-sm inline-block" style={{ background: "rgba(255,255,255,0.06)" }} />
        )}
        <span className="ml-auto flex items-center gap-1 text-zinc-600"><Clock size={10} />{timeAgo(s.last_updated)}</span>
      </div>
    </div>
  );
}

/* Sortable column header */
function SortableHead({ label, sortKey, activeSort, sortDir, onSort, className }) {
  const isActive = activeSort === sortKey;
  return (
    <TableHead
      className={`py-3 px-4 text-[10px] font-mono uppercase tracking-[0.15em] text-right select-none cursor-pointer group/sort transition-colors hover:text-zinc-300 ${isActive ? "text-zinc-200" : "text-zinc-500"} ${className || ""}`}
      onClick={() => onSort(sortKey)}
      data-testid={`sort-${sortKey}`}
    >
      <span className="inline-flex items-center gap-1 justify-end">
        {label}
        <span className="inline-flex flex-col -space-y-1">
          <CaretUp size={8} weight="fill" className={isActive && sortDir === "asc" ? "text-zinc-100" : "text-zinc-600 opacity-40"} />
          <CaretDown size={8} weight="fill" className={isActive && sortDir === "desc" ? "text-zinc-100" : "text-zinc-600 opacity-40"} />
        </span>
      </span>
    </TableHead>
  );
}

export default function StockTable({ stocks, loading, onSelectStock, watchlist }) {
  const [quotes, setQuotes] = useState({});
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [sortBy, setSortBy] = useState(null); // "price" | "change" | null
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  const intervalRef = useRef(null);

  const handleSort = (key) => {
    if (sortBy === key) {
      if (sortDir === "desc") setSortDir("asc");
      else { setSortBy(null); setSortDir("desc"); } // 3rd click resets
    } else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  const fetchBatchQuotes = useCallback(async (tickers) => {
    if (!tickers.length) return;
    setQuotesLoading(true);
    try {
      const res = await axios.post(`${API}/stocks/batch-quotes`, { tickers });
      setQuotes((prev) => ({ ...prev, ...res.data }));
    } catch (e) {
      console.error("Batch quote fetch failed:", e);
    } finally {
      setQuotesLoading(false);
    }
  }, []);

  // Fetch quotes when stocks change
  useEffect(() => {
    if (!stocks.length) return;
    const tickers = stocks.map((s) => s.ticker);
    // Fetch in chunks of 30
    const chunks = [];
    for (let i = 0; i < tickers.length; i += 30) {
      chunks.push(tickers.slice(i, i + 30));
    }
    // Fetch first chunk immediately, rest with slight delay
    chunks.forEach((chunk, idx) => {
      setTimeout(() => fetchBatchQuotes(chunk), idx * 500);
    });

    // Auto-refresh every 5 minutes
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      chunks.forEach((chunk, idx) => {
        setTimeout(() => fetchBatchQuotes(chunk), idx * 500);
      });
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalRef.current);
  }, [stocks, fetchBatchQuotes]);

  if (loading) {
    return (
      <div className="py-20 text-center" data-testid="loading-state">
        <div className="inline-block w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: "rgba(255,255,255,0.12)", borderTopColor: "#e4e4e7" }} />
        <p className="text-xs font-mono text-zinc-500 mt-3">Loading...</p>
      </div>
    );
  }

  if (!stocks.length) {
    return (
      <div className="py-20 text-center" data-testid="empty-state">
        <p className="text-xs font-mono text-zinc-500">No stocks match the current filters.</p>
      </div>
    );
  }

  const handleStar = (ticker) => {
    if (watchlist?.isWatched(ticker)) watchlist.removeTicker(ticker);
    else watchlist?.addTicker(ticker);
  };

  // Sort stocks by price or change % using live quote data
  const sortedStocks = (() => {
    if (!sortBy) return stocks;
    const sorted = [...stocks].sort((a, b) => {
      const qa = quotes[a.ticker];
      const qb = quotes[b.ticker];
      let va = 0, vb = 0;
      if (sortBy === "price") {
        va = qa?.price || 0;
        vb = qb?.price || 0;
      } else {
        va = qa?.change_percent || 0;
        vb = qb?.change_percent || 0;
      }
      return sortDir === "desc" ? vb - va : va - vb;
    });
    return sorted;
  })();

  return (
    <>
      {/* Mobile view */}
      <div className="block sm:hidden mt-0 rounded-xl overflow-hidden" style={{ background: "#0D1117", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="stock-table-mobile">
        {/* Mobile sort buttons */}
        {Object.keys(quotes).length > 0 && (
          <div className="flex items-center gap-2 py-2 px-3 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }} data-testid="mobile-sort-controls">
            <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">Sort:</span>
            {[{ key: "price", label: "Price" }, { key: "change", label: "Change %" }].map((s) => (
              <button
                key={s.key}
                onClick={() => handleSort(s.key)}
                className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider rounded border transition-colors ${
                  sortBy === s.key ? "text-zinc-100" : "text-zinc-500"
                }`}
                style={{ borderColor: sortBy === s.key ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.08)", background: sortBy === s.key ? "rgba(255,255,255,0.06)" : "transparent" }}
                data-testid={`mobile-sort-${s.key}`}
              >
                {s.label} {sortBy === s.key ? (sortDir === "desc" ? "↓" : "↑") : ""}
              </button>
            ))}
            {sortBy && (
              <button onClick={() => { setSortBy(null); setSortDir("desc"); }} className="text-[9px] font-mono text-zinc-500 hover:text-zinc-300 transition-colors" data-testid="mobile-sort-clear">
                Clear
              </button>
            )}
          </div>
        )}
        {sortedStocks.map((s) => (
          <MobileCard key={s.ticker} s={s} starred={watchlist?.isWatched(s.ticker)} onSelectStock={onSelectStock} onStar={handleStar} quote={quotes[s.ticker]} />
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block mt-0 overflow-x-auto rounded-xl" style={{ background: "#0D1117", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="stock-table-wrapper">
        <Table className="w-full text-left">
          <TableHeader>
            <TableRow className="border-b hover:bg-transparent" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
              <TableHead className="py-3 px-2 w-8"></TableHead>
              <TableHead className="py-3 px-4 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500">Company</TableHead>
              <TableHead className="py-3 px-4 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500">Status</TableHead>
              <TableHead className="py-3 px-4 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">AAOIFI Score</TableHead>
              <SortableHead label="Price" sortKey="price" activeSort={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortableHead label="Change" sortKey="change" activeSort={sortBy} sortDir={sortDir} onSort={handleSort} />
              <TableHead className="py-3 px-4 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 hidden md:table-cell">Sector</TableHead>
              <TableHead className="py-3 px-4 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 hidden lg:table-cell text-right whitespace-nowrap">Market Cap</TableHead>
              <TableHead className="py-3 px-4 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 text-right whitespace-nowrap">Debt / MCap</TableHead>
              <TableHead className="py-3 px-4 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 text-right hidden sm:table-cell whitespace-nowrap">Int. / Rev</TableHead>
              <TableHead className="py-3 px-4 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 text-right hidden sm:table-cell whitespace-nowrap">AR / Assets</TableHead>
              <TableHead className="py-3 px-3 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 hidden lg:table-cell">Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedStocks.map((s) => {
              const starred = watchlist?.isWatched(s.ticker);
              const q = quotes[s.ticker];
              return (
                <TableRow
                  key={s.ticker}
                  className="border-b hover:bg-white/[0.025] transition-colors cursor-pointer group"
                  style={{ borderColor: "rgba(255,255,255,0.04)" }}
                  data-testid={`stock-row-${s.ticker}`}
                  onClick={() => onSelectStock(s)}
                >
                  <TableCell className="py-2.5 pl-3 pr-1 w-8">
                    <button onClick={(e) => { e.stopPropagation(); handleStar(s.ticker); }} className="p-0.5" data-testid={`star-${s.ticker}`}>
                      <Star size={14} weight={starred ? "fill" : "regular"}
                        className={starred ? "text-amber-500" : "text-zinc-700 group-hover:text-zinc-500"} />
                    </button>
                  </TableCell>
                  <TableCell className="py-2.5 px-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <CompanyLogo ticker={s.ticker} size={32} />
                      <div className="min-w-0">
                        <div className="text-[13px] font-mono font-semibold text-zinc-100 leading-tight">{s.ticker}</div>
                        <div className="text-[11px] text-zinc-500 truncate max-w-[160px] leading-tight mt-0.5">{s.company_name}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5 px-4">
                    <VerdictPill verdict={s.verdict} testId={`verdict-badge-${s.ticker}`} />
                  </TableCell>
                  <TableCell className="py-2.5 px-4">
                    <ScoreCell stock={s} testId={`score-${s.ticker}`} />
                  </TableCell>
                  <TableCell className="py-2.5 px-4 text-right">
                    <PriceCell quote={q} />
                  </TableCell>
                  <TableCell className="py-2.5 px-4 text-right">
                    <ChangeCell quote={q} />
                  </TableCell>
                  <TableCell className="py-2.5 px-4 text-[12px] text-zinc-400 hidden md:table-cell">{s.sector}</TableCell>
                  <TableCell className="py-2.5 px-4 text-[11.5px] font-mono text-zinc-200 text-right hidden lg:table-cell tabular-nums">{fmtCap(s.market_cap)}</TableCell>
                  <TableCell className="py-2.5 px-4 text-right">
                    <Ratio status={s.debt_status} value={s.debt_ratio} threshold={33} testId={`debt-badge-${s.ticker}`} />
                  </TableCell>
                  <TableCell className="py-2.5 px-4 text-right hidden sm:table-cell">
                    <Ratio status={s.interest_status} value={s.interest_ratio} threshold={5} testId={`interest-badge-${s.ticker}`} />
                  </TableCell>
                  <TableCell className="py-2.5 px-4 text-right hidden sm:table-cell">
                    <Ratio status={s.receivable_status} value={s.receivable_ratio} threshold={49} testId={`receivable-badge-${s.ticker}`} />
                  </TableCell>
                  <TableCell className="py-2.5 px-3 hidden lg:table-cell">
                    <span className="text-[10px] font-mono text-zinc-500 flex items-center gap-1 whitespace-nowrap">
                      <Clock size={10} />
                      {timeAgo(s.last_updated)}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
